import React from 'react';
import { useNavigate } from 'react-router-dom';
import SearchBar from './SearchBar';
import FolderTree from './FolderTree';
import ModeSelector from './ModeSelector';
import DomainList from './DomainList';
import SiteList from './SiteList';
import DocumentList from './DocumentList';
import '../styles/ChatSidebar.css';

function ChatSidebar({ conversations, currentId, onNewConversation, isOpen, onToggle, onRefresh, onFolderSelect, onConversationRenamed, mode, onModeChange, domains, domainsLoading, selectedDomainId, onDomainSelect, sites, sitesLoading, selectedSiteId, onSiteSelect, documents, documentsLoading, documentsError, selectedDocId, onDocSelect, onDocRetry }) {
  const navigate = useNavigate();

  const handleSelectConversation = (conversationId) => {
    navigate(`/chat/${conversationId}`); if (window.innerWidth <= 768) onToggle();
  };

  const handleMoveToFolder = async (conversationId, folderId) => {
    try {
      const response = await fetch(`/conversations/${conversationId}/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ folder_id: folderId })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Refresh conversations list to show updated folder assignments
          if (onRefresh) {
            await onRefresh();
          }
        } else {
          console.error('Move failed:', data);
        }
      } else {
        const error = await response.text();
        console.error('Failed to move conversation:', response.status, error);
      }
    } catch (error) {
      console.error('Failed to move conversation:', error);
    }
  };

  const handleRenameConversation = async (conversationId, newTitle) => {
    try {
      const response = await fetch(`/conversations/${conversationId}/rename`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title: newTitle })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          if (onRefresh) {
            await onRefresh();
          }
          if (onConversationRenamed) {
            onConversationRenamed(conversationId, newTitle);
          }
        } else {
          console.error('Rename failed:', data);
        }
      } else {
        const error = await response.text();
        console.error('Failed to rename conversation:', response.status, error);
      }
    } catch (error) {
      console.error('Failed to rename conversation:', error);
    }
  };

  const handlePinConversation = async (conversationId) => {
    try {
      const response = await fetch(`/conversations/${conversationId}/pin`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          if (onRefresh) {
            await onRefresh();
          }
        } else {
          console.error('Pin toggle failed:', data);
        }
      } else {
        const error = await response.text();
        console.error('Failed to pin conversation:', response.status, error);
      }
    } catch (error) {
      console.error('Failed to pin conversation:', error);
    }
  };

  const handleArchiveConversation = async (conversationId) => {
    try {
      const response = await fetch(`/conversations/${conversationId}/archive`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          if (onRefresh) {
            await onRefresh();
          }
        } else {
          console.error('Archive toggle failed:', data);
        }
      } else {
        const error = await response.text();
        console.error('Failed to archive conversation:', response.status, error);
      }
    } catch (error) {
      console.error('Failed to archive conversation:', error);
    }
  };

  return (
    <aside className={`chat-sidebar ${isOpen ? 'open' : 'closed'}`}>
      <div className="sidebar-header">
        <ModeSelector mode={mode || 'conversations'} onModeChange={onModeChange} />
        <button className="sidebar-close" onClick={onToggle}>
          ×
        </button>
      </div>

      {(!mode || mode === 'conversations') && (
        <>
          <SearchBar onSelectResult={handleSelectConversation} />

          <button onClick={onNewConversation} className="new-conversation-btn">
            <span className="plus-icon">+</span>
            New Conversation
          </button>

          <div className="conversation-list">
            {conversations.length === 0 ? (
              <p className="no-conversations">No conversations yet</p>
            ) : (
              <FolderTree
                conversations={conversations}
                selectedId={currentId}
                onSelect={handleSelectConversation}
                onMoveToFolder={handleMoveToFolder}
                onRenameConversation={handleRenameConversation}
                onPinConversation={handlePinConversation}
                onArchiveConversation={handleArchiveConversation}
                onFolderSelect={(folderId, folderName) => onFolderSelect && onFolderSelect(folderId, folderName)}
              />
            )}
          </div>
        </>
      )}

      {mode === 'domains' && (
        <DomainList
          domains={domains}
          selectedId={selectedDomainId}
          onSelect={onDomainSelect}
          loading={domainsLoading}
        />
      )}

      {mode === 'sites' && (
        <SiteList
          sites={sites}
          selectedId={selectedSiteId}
          onSelect={onSiteSelect}
          loading={sitesLoading}
        />
      )}

      {mode === 'documents' && (
        <DocumentList
          documents={documents}
          selectedId={selectedDocId}
          onSelect={onDocSelect}
          loading={documentsLoading}
          error={documentsError}
          onRetry={onDocRetry}
        />
      )}
    </aside>
  );
}

export default ChatSidebar;
